import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { controlBase } from '../primitives/input.js';

export interface TagInputProps {
  value: readonly string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  /** Maximum number of tags. */
  max?: number;
  /** Maximum characters per tag. */
  maxLength?: number;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

/**
 * Free-text tags (e.g. amenities). Enter or comma adds the typed text; Backspace on an empty field removes the
 * last tag; each chip has a labelled remove button. Duplicates (case-insensitive) are ignored.
 */
export const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(function TagInput(
  { value, onChange, placeholder = 'Escribe y presiona Enter', max, maxLength = 120, disabled, id, className, ...aria },
  ref,
) {
  const [draft, setDraft] = React.useState('');
  const atMax = max !== undefined && value.length >= max;

  const commit = (raw: string) => {
    const parts = raw
      .split(',')
      .map((p) => p.trim().slice(0, maxLength))
      .filter(Boolean);
    if (parts.length === 0) return;
    const next = [...value];
    for (const p of parts) {
      if (max !== undefined && next.length >= max) break;
      if (!next.some((v) => v.toLowerCase() === p.toLowerCase())) next.push(p);
    }
    onChange(next);
    setDraft('');
  };

  return (
    <div
      className={cn(
        controlBase,
        'flex h-auto min-h-10 flex-wrap items-center gap-1.5 py-1.5 focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-focus',
        disabled && 'cursor-not-allowed bg-surface-subtle opacity-60',
        className,
      )}
    >
      {value.map((tag) => (
        <span key={tag} className="inline-flex max-w-full items-center gap-1 rounded-pill border border-border-subtle bg-surface-subtle py-0.5 pl-2.5 pr-1 text-helper text-text-primary">
          <span className="truncate">{tag}</span>
          {!disabled ? (
            <button
              type="button"
              aria-label={`Quitar ${tag}`}
              onClick={() => onChange(value.filter((v) => v !== tag))}
              className="inline-flex size-5 items-center justify-center rounded-pill text-text-muted hover:bg-surface-default hover:text-text-primary focus-visible:outline-2 focus-visible:outline-focus"
            >
              <X aria-hidden className="size-3.5" />
            </button>
          ) : null}
        </span>
      ))}
      <input
        ref={ref}
        id={id}
        value={draft}
        disabled={disabled || atMax}
        placeholder={atMax ? `Máximo ${max}` : placeholder}
        maxLength={maxLength}
        onChange={(e) => {
          const v = e.target.value;
          if (v.includes(',')) commit(v);
          else setDraft(v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(draft);
          } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => commit(draft)}
        className="min-w-32 flex-1 bg-transparent text-body text-text-primary outline-none placeholder:text-text-muted"
        {...aria}
      />
    </div>
  );
});
